/* Product page: variants, gallery, zoom, sticky add to cart, share, recommendations. */
(() => {
  const theme = window.theme;

  /* ---------- Recommendations (related + complementary) ---------- */
  if (!customElements.get('product-recommendations')) {
    class ProductRecommendations extends HTMLElement {
      connectedCallback() {
        if (!this.dataset.url) return;
        const load = () => fetch(this.dataset.url)
          .then((r) => r.text())
          .then((html) => {
            const fresh = theme.parseHTML(html).querySelector('product-recommendations');
            if (fresh && fresh.innerHTML.trim()) {
              this.innerHTML = fresh.innerHTML;
              const heading = this.dataset.heading && this.querySelector('.eyebrow');
              if (heading) heading.textContent = this.dataset.heading;
              theme.observeReveal?.(this);
              this.closest('[data-recommendations-wrap]')?.classList.remove('section--pending');
            } else {
              this.closest('[data-recommendations-wrap]')?.setAttribute('hidden', '');
            }
          })
          .catch(() => {});
        new IntersectionObserver((entries, obs) => {
          if (entries[0].isIntersecting) { obs.disconnect(); load(); }
        }, { rootMargin: '0px 0px 600px 0px' }).observe(this);
      }
    }
    customElements.define('product-recommendations', ProductRecommendations);
  }

  /* ---------- Product info ---------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl;
      this.cache = new Map();
      this.track = this.querySelector('[data-gallery-track]');

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-value], [data-option-select]')) this.onOptionChange(e.target);
      });

      this.initGallery();
      this.initZoom();
      this.initSticky();
      this.initShare();
    }

    selectedOptionIds() {
      const ids = [];
      this.querySelectorAll('variant-picker .picker__group').forEach((group) => {
        const checked = group.querySelector('[data-option-value]:checked');
        const select = group.querySelector('[data-option-select]');
        if (checked) ids.push(checked.value);
        else if (select) ids.push(select.value);
      });
      return ids;
    }

    async onOptionChange(input) {
      const targetUrl = input.dataset.productUrl || input.selectedOptions?.[0]?.dataset.productUrl || this.productUrl;
      const ids = this.selectedOptionIds().join(',');

      // Combined listing: the value belongs to a sibling product — go there.
      if (targetUrl && targetUrl !== this.productUrl) {
        location.href = `${targetUrl}?option_values=${ids}`;
        return;
      }

      // Update the selected label immediately for snappy feedback.
      const legend = input.closest('.picker__group')?.querySelector('.picker__selected');
      if (legend && input.dataset.value) legend.textContent = input.dataset.value;

      this.classList.add('is-updating');
      const url = `${this.productUrl}?option_values=${ids}&section_id=${this.sectionId}`;
      // Only the latest selection may render: abort anything still in flight.
      this.controller?.abort();
      const controller = (this.controller = new AbortController());
      try {
        let html = this.cache.get(url);
        if (!html) {
          html = await (await fetch(url, { signal: controller.signal })).text();
          this.cache.set(url, html);
        }
        if (controller !== this.controller) return;
        this.render(theme.parseHTML(html));
      } catch (e) {
        if (e.name !== 'AbortError') location.href = `${this.productUrl}?option_values=${ids}`;
      } finally {
        if (controller === this.controller) this.classList.remove('is-updating');
      }
    }

    render(doc) {
      const focusedId = document.activeElement?.id;
      doc.querySelectorAll('[data-update]').forEach((fresh) => {
        const current = this.querySelector(`[data-update="${fresh.dataset.update}"]`);
        if (current) current.innerHTML = fresh.innerHTML;
      });
      if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });

      const variantJson = this.querySelector('[data-selected-variant]')?.textContent;
      const variant = variantJson ? JSON.parse(variantJson) : null;
      if (!variant) return;

      this.querySelectorAll('[data-variant-id]').forEach((input) => { input.value = variant.id; });
      this.querySelectorAll('[data-payment-button]').forEach((el) => { el.hidden = !variant.available; });
      const installments = document.querySelector('#InstallmentsForm input[name="id"]');
      if (installments) installments.value = variant.id;

      if (this.dataset.updateUrl === 'true') {
        history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);
      }
      if (variant.featured_media) this.showMedia(variant.featured_media.id);
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant } }));
    }

    /* ---------- Gallery ---------- */
    initGallery() {
      if (!this.track) return;
      const items = [...this.track.children];
      const counter = this.querySelector('[data-gallery-current]');
      const dots = [...this.querySelectorAll('.product-gallery__dots span')];
      const thumbs = [...this.querySelectorAll('[data-thumb]')];

      const setActive = (index) => {
        this.activeIndex = index;
        if (counter) counter.textContent = index + 1;
        dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
        thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
      };

      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) setActive(Number(entry.target.dataset.index));
        });
      }, { root: this.track, threshold: [0.6] });
      items.forEach((item) => io.observe(item));

      const go = (dir) => {
        const next = Math.min(items.length - 1, Math.max(0, (this.activeIndex || 0) + dir));
        this.scrollToIndex(next);
      };
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => go(-1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => go(1));
      thumbs.forEach((t) => t.addEventListener('click', () => this.scrollToIndex(Number(t.dataset.thumb))));

      // Open on the selected variant's photo (e.g. the first available color), not always photo 1.
      const start = items.findIndex((item) => item.classList.contains('is-active'));
      if (start > 0) {
        if (this.isCarousel()) this.track.scrollLeft = items[start].offsetLeft - this.track.offsetLeft;
        setActive(start);
      } else {
        setActive(0);
      }
    }

    isCarousel() {
      return this.track && this.track.scrollWidth > this.track.clientWidth + 4;
    }

    scrollToIndex(index) {
      const item = this.track?.children[index];
      if (!item) return;
      if (this.isCarousel()) {
        this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      }
      // Highlight the matching thumbnail right away (the observer may lag on hidden overflow).
      this.querySelectorAll('[data-thumb]').forEach((t) => t.classList.toggle('is-active', Number(t.dataset.thumb) === index));
      this.activeIndex = index;
    }

    showMedia(mediaId) {
      const item = this.track?.querySelector(`[data-media-id="${mediaId}"]`);
      if (!item) return;
      if (this.isCarousel()) {
        this.scrollToIndex(Number(item.dataset.index));
      } else if (item.getBoundingClientRect().top > innerHeight || item.getBoundingClientRect().bottom < 0) {
        // Desktop grid: bring the variant's image into view only if it's off screen.
        item.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    /* ---------- Zoom ---------- */
    initZoom() {
      const dialog = this.querySelector('.zoom-dialog');
      if (!dialog) return;
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-zoom]');
        if (!btn) return;
        // On mobile carousels, a swipe can register as a click — ignore while scrolling.
        if (this.track?.dataset.scrolling) return;
        theme.openDialog(dialog.id, btn);
        const target = dialog.querySelector(`[data-zoom-index="${btn.dataset.zoom}"]`);
        requestAnimationFrame(() => target?.scrollIntoView({ block: 'start' }));
      });
      dialog.addEventListener('click', (e) => { if (e.target.tagName === 'IMG') dialog.close(); });
      let t;
      this.track?.addEventListener('scroll', () => {
        this.track.dataset.scrolling = '1';
        clearTimeout(t);
        t = setTimeout(() => delete this.track.dataset.scrolling, 120);
      }, { passive: true });
    }

    /* ---------- Sticky add to cart ---------- */
    initSticky() {
      const bar = this.querySelector('[data-sticky-atc]');
      const buy = this.querySelector('[data-buy-buttons]');
      if (!bar || !buy) return;
      const footer = document.querySelector('.footer');
      let pastBuy = false;
      let atFooter = false;
      const sync = () => {
        const show = pastBuy && !atFooter;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
        bar.querySelector('button')?.setAttribute('tabindex', show ? '0' : '-1');
        document.body.classList.toggle('has-sticky-atc', show);
      };
      new IntersectionObserver(([entry]) => {
        pastBuy = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sync();
      }).observe(buy);
      if (footer) {
        new IntersectionObserver(([entry]) => { atFooter = entry.isIntersecting; sync(); }).observe(footer);
      }
    }

    /* ---------- Share ---------- */
    initShare() {
      this.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-share]');
        if (!btn) return;
        const data = { title: btn.dataset.title, url: btn.dataset.url };
        try {
          if (navigator.share) await navigator.share(data);
          else {
            await navigator.clipboard.writeText(data.url);
            theme.toast(window.theme.strings.linkCopied || 'Link copied');
          }
        } catch (err) {}
      });
    }
  }
  if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);
})();

/* ---------- Reviews side panel: borrows the reviews app section from the page ---------- */
(() => {
  document.querySelectorAll('[data-reviews-drawer]').forEach((drawer) => {
    const source = document.getElementById(drawer.dataset.reviewsDrawer);
    const body = drawer.querySelector('[data-reviews-body]');
    // No reviews section on this template: hide the openers so they never lead nowhere.
    if (!source || !source.querySelector('.shopify-app-block, [class*="oke"]')) {
      document.querySelectorAll(`[data-open="${drawer.id}"]`).forEach((el) => el.remove());
      return;
    }
    drawer.addEventListener('dialog:open', () => {
      if (!body.childElementCount) {
        const content = source.querySelector('.page-width') || source;
        body.append(...content.childNodes);
      }
      body.scrollTop = 0;
      // Let the reviews widget re-measure now that it is visible.
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    });
  });
})();

/* ---------- Back-in-stock pop-up (Kbite app): restyled to the Blvck look ---------- */
// Kbite renders its form in a same-origin iframe; when it opens we hand it the theme's fonts,
// a stylesheet and the product/size being requested. The form logic itself is untouched.
(() => {
  const fontFaces = () => {
    let css = '';
    for (const sheet of document.styleSheets) {
      try { for (const rule of sheet.cssRules) if (rule instanceof CSSFontFaceRule) css += rule.cssText; } catch (e) { /* cross-origin sheet */ }
    }
    return css;
  };
  const root = getComputedStyle(document.documentElement);
  const heading = root.getPropertyValue('--font-heading') || 'serif';
  const body = root.getPropertyValue('--font-body') || 'sans-serif';
  const css = `
    * { color: #0b0b0b; font-family: ${body} !important; }
    body.fade-in { background: rgb(0 0 0 / 0.45) !important; }
    .modal-container {
      width: min(460px, calc(100vw - 32px)) !important; padding: 3rem 2.5rem 2.25rem !important; border: 0 !important; border-radius: 0 !important;
      box-shadow: 0 30px 80px rgb(0 0 0 / 0.25); animation: blvck-rise .5s cubic-bezier(.2,.7,.2,1) both;
    }
    @keyframes blvck-rise { from { opacity: 0; transform: translate(-50%, calc(-50% + 24px)); } }
    .blvck-eyebrow { display: block; margin: 0 0 .875rem; font-size: 10px; font-weight: 500; letter-spacing: .2em; text-transform: uppercase; color: #8a8a8a; }
    .modal-title { margin: 0 0 .75rem !important; font-family: ${heading} !important; font-weight: 400 !important; font-size: 30px !important; line-height: 1.1 !important; text-transform: lowercase; letter-spacing: 0; }
    .modal-title::first-letter { text-transform: uppercase; }
    .notification-message { margin: 0 0 1.75rem !important; font-size: 13px !important; line-height: 1.6; color: #6b6b6b !important; }
    .blvck-product { display: flex; align-items: center; gap: .875rem; margin: 0 0 1.5rem; padding: .875rem 0; border-block: 1px solid #ececec; }
    .blvck-product img { width: 52px; height: 52px; object-fit: cover; background: #f3f3f3; flex-shrink: 0; }
    .blvck-product strong { display: block; font-size: 13px; font-weight: 500; line-height: 1.35; }
    .blvck-product span { display: block; margin-top: 2px; font-size: 11px; letter-spacing: .12em; text-transform: uppercase; color: #8a8a8a; }
    .email-input, .vue-tel-input {
      height: 52px !important; margin: 0 0 .625rem !important; padding: 0 1rem !important; border: 1px solid #d6d6d6 !important; border-radius: 0 !important;
      background: #fff !important; font-size: 14px !important; color: #0b0b0b !important; transition: border-color .2s;
    }
    .email-input::placeholder { color: #9a9a9a; }
    .email-input:focus { outline: none !important; border-color: #0b0b0b !important; }
    .notify-button {
      height: 52px; margin-top: 0 !important; padding: 0 1rem !important; border: 1px solid #0b0b0b !important; border-radius: 0 !important;
      background: #0b0b0b !important; color: #fff !important; font-size: 11px !important; font-weight: 500 !important; letter-spacing: .2em; text-transform: uppercase;
      transition: background .25s, color .25s;
    }
    .notify-button:hover { background: #fff !important; color: #0b0b0b !important; }
    .close-icon {
      position: absolute; top: 14px; right: 14px; float: none !important; width: 40px; height: 40px; margin: 0 !important; font-size: 0 !important;
      opacity: 1 !important; color: transparent !important; text-shadow: none !important;
    }
    .close-icon::before, .close-icon::after { content: ''; position: absolute; top: 50%; left: 50%; width: 18px; height: 1px; background: #0b0b0b; }
    .close-icon::before { transform: translate(-50%, -50%) rotate(45deg); }
    .close-icon::after { transform: translate(-50%, -50%) rotate(-45deg); }
    .close-icon:hover::before, .close-icon:hover::after { background: #6b6b6b; }
    .gw-success-message { margin: 1rem 0 0 !important; padding: .875rem 1rem !important; border: 1px solid #0b0b0b !important; border-radius: 0 !important; background: #fff !important; font-size: 13px !important; }
    .gw-success-message * { color: #0b0b0b !important; }
    .gw-success-message .close-text { margin-left: .5rem; text-decoration: underline; text-underline-offset: 3px; }
    .alert-danger, .gw-error-message { border-radius: 0 !important; font-size: 12px !important; }
    .privacy-text { margin: 1rem 0 0 !important; font-size: 11px !important; line-height: 1.5; color: #9a9a9a !important; }
    @media (max-width: 600px) {
      .modal-container { top: auto !important; bottom: 0 !important; left: 0 !important; width: 100% !important; transform: none !important; padding: 2.5rem 1.25rem calc(1.75rem + env(safe-area-inset-bottom)) !important; animation-name: blvck-sheet; }
      @keyframes blvck-sheet { from { transform: translateY(100%); } }
      .modal-title { font-size: 26px !important; }
    }`;

  const style = (frame) => {
    const doc = frame.contentDocument;
    if (!doc?.head) return;
    if (doc.getElementById('blvck-kbite')) return decorate(doc);
    const el = doc.createElement('style');
    el.id = 'blvck-kbite';
    el.textContent = fontFaces() + css;
    doc.head.appendChild(el);
    decorate(doc);
  };

  // Adds an eyebrow and the product being requested; idempotent, so it can re-run after Vue renders.
  const decorate = (doc) => {
    // Click on the dimmed backdrop (anything outside the panel) or Esc closes it, like the theme's drawers.
    if (!doc.blvckDismiss) {
      doc.blvckDismiss = true;
      const close = () => doc.querySelector('.close-icon')?.click();
      doc.addEventListener('click', (e) => { if (doc.querySelector('.modal-container') && !e.target.closest('.modal-container')) close(); });
      doc.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    }
    const container = doc.querySelector('.modal-container');
    const title = doc.querySelector('.modal-title');
    if (container && title && !doc.querySelector('.blvck-eyebrow')) {
      const eyebrow = doc.createElement('span');
      eyebrow.className = 'blvck-eyebrow';
      eyebrow.textContent = 'Back in stock alert';
      title.before(eyebrow);
      const button = document.querySelector('.gw-button-widget');
      const info = button?.closest('product-info') || document;
      const name = info.querySelector('.product__title')?.textContent.trim();
      const size = [...info.querySelectorAll('.picker__input:checked')].map((i) => info.querySelector(`label[for="${i.id}"]`)?.childNodes[0]?.textContent.trim()).filter(Boolean).join(' / ');
      const img = info.querySelector('.product-gallery__item.is-active img, .product-gallery img');
      if (name) {
        const product = doc.createElement('div');
        product.className = 'blvck-product';
        product.innerHTML = `${img ? `<img src="${img.currentSrc || img.src}" alt="">` : ''}<div><strong></strong>${size ? '<span></span>' : ''}</div>`;
        product.querySelector('strong').textContent = name;
        if (size) product.querySelector('span').textContent = size;
        doc.querySelector('.notification-message')?.after(product);
      }
    }
  };

  const watch = (frame) => {
    frame.style.backdropFilter = 'blur(3px)';
    style(frame);
    frame.addEventListener('load', () => style(frame));
    // Vue renders the form a moment after load; retry briefly.
    let tries = 0;
    const timer = setInterval(() => { style(frame); if (++tries > 40) clearInterval(timer); }, 100);
  };
  new MutationObserver((records) => {
    for (const r of records) for (const n of r.addedNodes) if (n.nodeType === 1 && n.matches?.('iframe.gw-modal')) watch(n);
  }).observe(document.body, { childList: true });
  document.querySelectorAll('iframe.gw-modal').forEach(watch);
})();

/* ---------- "Notify me" button: opens the back-in-stock app's form (waits for it if still loading) ---------- */
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-notify-me]');
  if (!button) return;
  const scope = button.closest('.product-form__buttons') || document;
  const open = () => {
    const app = scope.querySelector('.gw-button-widget') || document.querySelector('.gw-button-widget');
    if (!app) return false;
    app.click();
    return true;
  };
  if (open()) return;
  button.classList.add('is-loading');
  let tries = 0;
  const timer = setInterval(() => {
    if (open() || ++tries > 80) {
      clearInterval(timer);
      button.classList.remove('is-loading');
      if (tries > 80) window.theme?.toast?.(window.theme.strings?.cartError || 'Please try again.');
    }
  }, 100);
});

/* ---------- EU legal guarantee pop-up (Pandectes app): restyled to the Blvck look ---------- */
// The app renders in an open shadow root; we add a stylesheet and a heading once it appears.
(() => {
  const css = `
    .eu-warranty-trigger { font: inherit !important; letter-spacing: 0.01em; text-underline-offset: 3px; cursor: pointer; }
    .eu-warranty-modal {
      width: min(440px, calc(100vw - 32px)) !important; max-width: none !important; max-height: calc(100dvh - 48px) !important;
      padding: 0 !important; border: 0 !important; border-radius: 0 !important; background: #fff !important; color: #0b0b0b !important;
      box-shadow: 0 30px 80px rgb(0 0 0 / 0.25) !important; overflow: auto;
    }
    .eu-warranty-modal[open] { animation: blvck-rise .5s cubic-bezier(.2,.7,.2,1) both; }
    @keyframes blvck-rise { from { opacity: 0; transform: translateY(24px); } }
    .eu-warranty-modal::backdrop { background: rgb(0 0 0 / 0.45) !important; backdrop-filter: blur(3px); }
    .eu-warranty-modal form { position: absolute; top: 12px; right: 12px; margin: 0; z-index: 1; }
    .eu-warranty-modal-close {
      position: relative; width: 40px !important; height: 40px !important; padding: 0 !important; border: 0 !important; border-radius: 0 !important;
      background: transparent !important; font-size: 0 !important; color: transparent !important; cursor: pointer; box-shadow: none !important;
    }
    .eu-warranty-modal-close::before, .eu-warranty-modal-close::after {
      content: ''; position: absolute; top: 50%; left: 50%; width: 18px; height: 1px; background: #0b0b0b; transition: transform .3s cubic-bezier(.2,.7,.2,1);
    }
    .eu-warranty-modal-close::before { transform: translate(-50%, -50%) rotate(45deg); }
    .eu-warranty-modal-close::after { transform: translate(-50%, -50%) rotate(-45deg); }
    .eu-warranty-modal-close:hover::before { transform: translate(-50%, -50%) rotate(135deg); }
    .eu-warranty-modal-close:hover::after { transform: translate(-50%, -50%) rotate(45deg); }
    .eu-warranty-modal-body { padding: 3rem 2.5rem 2.25rem !important; margin: 0 !important; }
    .blvck-eyebrow { display: block; margin: 0 0 .75rem; font-size: 10px; font-weight: 500; letter-spacing: .2em; text-transform: uppercase; color: #8a8a8a; }
    .blvck-title { margin: 0 0 .75rem; font-family: var(--font-heading, serif); font-weight: 400; font-size: 30px; line-height: 1.1; color: #0b0b0b; }
    .blvck-text { margin: 0 0 1.75rem; font-size: 13px; line-height: 1.65; color: #6b6b6b; }
    .eu-warranty-label { display: grid !important; gap: 1.5rem; margin: 0 !important; padding: 0 !important; border: 0 !important; background: none !important; }
    .eu-warranty-label-art { margin: 0 !important; padding: 1.25rem !important; background: #f5f5f5 !important; border: 0 !important; border-radius: 0 !important; display: grid; place-items: center; }
    .eu-warranty-label-image { display: block; width: auto !important; max-width: 100% !important; max-height: 46vh !important; height: auto !important; border-radius: 0 !important; box-shadow: none !important; }
    .eu-warranty-label-cta {
      display: flex !important; align-items: center; justify-content: center; min-height: 52px; padding: 0 1rem !important; margin: 0 !important;
      border: 1px solid #0b0b0b !important; border-radius: 0 !important; background: transparent !important; color: #0b0b0b !important;
      font-family: var(--font-body, sans-serif) !important; font-size: 11px !important; font-weight: 500 !important; letter-spacing: .2em !important;
      text-transform: uppercase !important; text-decoration: none !important; text-align: center; transition: background .25s, color .25s;
    }
    .eu-warranty-label-cta:hover { background: #0b0b0b !important; color: #fff !important; }
    @media (max-width: 600px) {
      .eu-warranty-modal { width: 100vw !important; max-height: 88dvh !important; margin: auto 0 0 !important; animation-name: blvck-sheet !important; }
      @keyframes blvck-sheet { from { transform: translateY(100%); } }
      .eu-warranty-modal-body { padding: 2.75rem 1.25rem calc(1.75rem + env(safe-area-inset-bottom)) !important; }
      .blvck-title { font-size: 26px; }
    }`;
  const texts = {
    en: ['Legal guarantee', 'Your rights as a customer', 'Every purchase is covered by the EU legal guarantee of conformity for at least two years. Here is the official notice.'],
    fr: ['Garantie légale', 'Vos droits de consommateur', 'Chaque achat bénéficie de la garantie légale de conformité de l’UE pendant au moins deux ans. Voici la notice officielle.'],
  };
  const style = (host) => {
    const root = host.shadowRoot;
    if (!root || !root.querySelector('.eu-warranty-modal')) return false;
    if (!root.getElementById('blvck-warranty')) {
      const el = document.createElement('style');
      el.id = 'blvck-warranty';
      el.textContent = css;
      root.appendChild(el);
    }
    const body = root.querySelector('.eu-warranty-modal-body');
    if (body && !root.querySelector('.blvck-title')) {
      const [eyebrow, title, text] = texts[(document.documentElement.lang || 'en').slice(0, 2)] || texts.en;
      body.insertAdjacentHTML('afterbegin', `<span class="blvck-eyebrow"></span><h2 class="blvck-title"></h2><p class="blvck-text"></p>`);
      body.querySelector('.blvck-eyebrow').textContent = eyebrow;
      body.querySelector('.blvck-title').textContent = title;
      body.querySelector('.blvck-text').textContent = text;
    }
    // Backdrop click closes, like the theme's own dialogs.
    const modal = root.querySelector('.eu-warranty-modal');
    if (modal && !modal.blvckBound) {
      modal.blvckBound = true;
      modal.addEventListener('click', (e) => { if (e.target === modal) modal.close(); });
    }
    return true;
  };
  const scan = () => document.querySelectorAll('pandectes-eu-warranty').forEach(style);
  let tries = 0;
  const timer = setInterval(() => { scan(); if (++tries > 60) clearInterval(timer); }, 250);
  document.addEventListener('click', (e) => { const host = e.target.closest?.('pandectes-eu-warranty'); if (host) style(host); }, true);
})();

/* ---------- Fit finder (True Fit): tag every True Fit element (widget, pop-up) so the theme variables apply ---------- */
(() => {
  // A few details the app's variables don't cover, added inside its (open) shadow root.
  const css = `
    .MuiIconButton-root { background: transparent !important; border-radius: 0 !important; box-shadow: none !important; }
    .MuiChip-root { border-radius: 0 !important; }
    .MuiLinearProgress-root, .MuiLinearProgress-bar { border-radius: 0 !important; }
    .MuiStack-root:has(> .MuiButton-text) { position: absolute !important; width: 1px !important; height: 1px !important; overflow: hidden !important; clip-path: inset(50%) !important; }
    .MuiButton-text { padding: 0 !important; min-width: 0 !important; text-transform: none !important; background: none !important; justify-content: flex-start !important; text-align: left !important; }
    .MuiButton-text .MuiTypography-caption { font-family: var(--font-body, sans-serif) !important; font-size: 12px !important; line-height: 1.5 !important; letter-spacing: 0.01em !important; color: #6b6b6b !important; font-weight: 400 !important; }
    .MuiButton-text svg + .MuiTypography-caption { color: #0b0b0b !important; font-weight: 500 !important; }
    .MuiButton-text svg { width: 13px !important; height: 13px !important; }
    .MuiButton-text > .MuiStack-root:last-child > .MuiStack-root { flex-direction: row !important; flex-wrap: wrap !important; align-items: center !important; column-gap: 0.5rem !important; }
    .MuiButton-text > .MuiStack-root:last-child > .MuiStack-root > * + *::before { content: '·'; margin-right: 0.5rem; color: #b5b5b5; }
    .MuiButton-text > .MuiStack-root:last-child > .MuiStack-root > .MuiStack-root { column-gap: 0.5rem !important; }
    .MuiButton-text .MuiTypography-caption + .MuiTypography-caption::before { content: '·'; margin-right: 0.5rem; color: #b5b5b5; display: inline-block; text-decoration: none; }
    @media (max-width: 600px) {
      .MuiButton-text .MuiTypography-caption:has(+ .MuiTypography-caption) { display: none !important; }
      .MuiButton-text .MuiTypography-caption + .MuiTypography-caption::before { content: none; }
      .MuiButton-text > .MuiStack-root:last-child > .MuiStack-root { flex-direction: column !important; align-items: flex-start !important; row-gap: 0.125rem !important; }
      .MuiButton-text > .MuiStack-root:last-child > .MuiStack-root > * + *::before { content: none !important; }
      .MuiButton-text > .MuiStack-root:last-child > .MuiStack-root > * + * { padding-left: 1.3rem; }
    }
    .MuiButton-text .MuiTypography-caption + .MuiTypography-caption { color: #0b0b0b !important; text-decoration: underline; text-underline-offset: 3px; text-decoration-color: rgb(11 11 11 / 0.35); }
    .MuiButton-text:hover .MuiTypography-caption + .MuiTypography-caption { text-decoration-color: #0b0b0b; }
    h1.MuiTypography-h1 {
      font-family: var(--font-heading, serif) !important; font-weight: 400 !important; font-size: 1.625rem !important; line-height: 1.15 !important; letter-spacing: 0 !important;
    }`;
  const dress = (host) => {
    const root = host.shadowRoot;
    if (!root || root.getElementById('blvck-truefit')) return;
    const el = document.createElement('style');
    el.id = 'blvck-truefit';
    el.textContent = css;
    root.appendChild(el);
  };
  const scan = () => document.querySelectorAll('tf-recommendation').forEach(dress);
  let tries = 0;
  const timer = setInterval(() => { scan(); if (++tries > 200 || document.querySelector('tf-recommendation')?.shadowRoot?.getElementById('blvck-truefit')) clearInterval(timer); }, 150);
  const tag = (root) => root.querySelectorAll?.('*').forEach((el) => { if (el.tagName.startsWith('TF-')) el.classList.add('tf-host'); });
  new MutationObserver((records) => {
    for (const r of records) for (const n of r.addedNodes) {
      if (n.nodeType !== 1) continue;
      if (n.tagName.startsWith('TF-')) n.classList.add('tf-host');
      tag(n);
    }
  }).observe(document.body, { childList: true, subtree: true });
  tag(document);
})();

/* ---------- Fit tools: "Size guide" opens Kiwi; True Fit note keeps its place until ready ---------- */
(() => {
  const row = document.querySelector('[data-fit-row]');
  const link = document.querySelector('[data-kiwi-open]');
  const kiwiLink = () => document.querySelector('[data-kiwi-chart] .ks-chart-modal-link, .ks-chart-modal-link');

  if (link) {
    link.addEventListener('click', () => {
      if (kiwiLink()) return kiwiLink().click();
      link.classList.add('is-loading');
      let tries = 0;
      const timer = setInterval(() => {
        if (kiwiLink() || ++tries > 60) { clearInterval(timer); link.classList.remove('is-loading'); kiwiLink()?.click(); }
      }, 100);
    });
    // No chart for this product in Kiwi: remove the link once Kiwi has had time to answer.
    window.addEventListener('load', () => setTimeout(() => { if (!kiwiLink()) link.closest('.picker__chart, .picker__legend > *')?.classList.add('is-empty'); }, 6000));
  }

  // True Fit: the theme's note is shown at once; swap in True Fit's insight when its widget has data.
  const note = row?.querySelector('[data-fit-note]');
  const slot = row?.querySelector('[data-truefit]');
  if (note && slot) {
    const text = note.querySelector('[data-fit-text]');
    const cta = note.querySelector('[data-fit-open]');
    const widget = () => { const tf = slot.querySelector('tf-recommendation'); return tf?.shadowRoot?.querySelector('.MuiButton-text .MuiTypography-caption') ? tf : null; };
    const tfButton = () => widget()?.shadowRoot?.querySelector('.MuiButton-text');
    // Read True Fit's line as it lays it out (each visual line / block = one part), drop its own CTA text,
    // then show: lead · detail · detail. Re-syncs if True Fit updates (e.g. after its quiz).
    let shown = '';
    const parts = (tf) => {
      const out = [];
      [...tf.shadowRoot.querySelectorAll('.MuiButton-text .MuiTypography-caption')].filter((c) => !c.parentElement.closest('.MuiTypography-caption')).forEach((caption) => {
        // each child node (text or element) is a candidate part; line breaks split too
        const chunks = [...caption.childNodes].flatMap((n) => (n.nodeType === 1 ? (n.innerText || n.textContent) : n.textContent).split('\n'));
        let merged = [];
        for (const raw of chunks) {
          const t = raw.replace(/\s+/g, ' ').trim();
          if (!t) continue;
          // "Size:" + "S" -> "Size: S"
          if (merged.length && /:$/.test(merged[merged.length - 1])) merged[merged.length - 1] += ' ' + t;
          else merged.push(t);
        }
        out.push(...merged);
      });
      // True Fit repeats some text (e.g. a second copy of the recommendation): keep each part once
      const seen = new Set();
      return out.filter((t) => {
        const k = t.toLowerCase();
        if (/what.?s my size|quelle est ma taille/i.test(t) || seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    };
    const swap = () => {
      const tf = widget(); if (!tf) return false;
      const list = parts(tf);
      if (!list.length) return false;
      const key = list.join('|');
      if (key === shown) return true;
      const first = !shown;
      shown = key;
      const render = () => {
        text.replaceChildren(...list.map((t, n) => {
          const el = document.createElement('span');
          el.className = n === 0 ? 'fit-note__lead' : /\d/.test(t) ? 'fit-note__count' : 'fit-note__detail';
          el.textContent = t;
          return el;
        }));
        text.classList.remove('is-swapping');
      };
      if (first) { text.classList.add('is-swapping'); setTimeout(render, 250); } else render();
      // keep in sync with later changes inside True Fit
      if (!tf.blvckObserved) { tf.blvckObserved = true; new MutationObserver(() => swap()).observe(tf.shadowRoot, { subtree: true, childList: true, characterData: true }); }
      return true;
    };
    let polls = 0;
    const poll = setInterval(() => { if (swap() || ++polls > 300) clearInterval(poll); }, 200);
    cta.addEventListener('click', () => {
      if (tfButton()) return tfButton().click();
      cta.classList.add('is-loading');
      let tries = 0;
      const timer = setInterval(() => {
        const b = tfButton();
        if (b || ++tries > 40) {
          clearInterval(timer); cta.classList.remove('is-loading');
          if (b) b.click();
          else (document.querySelector('[data-kiwi-open]') || document.querySelector('[data-open^="SizeChart-"]'))?.click();
        }
      }, 100);
    });
  }
})();

/* ---------- Pre-order (Essential app): the sticky bar uses the same label as the pre-order button ---------- */
(() => {
  const info = document.querySelector('product-info');
  if (!info) return;
  const sync = () => {
    const pre = info.querySelector('.essential-preorder-container-active button');
    const sticky = info.querySelector('[data-update="sticky-button"] button[type="submit"]');
    if (!sticky) return;
    if (pre && pre.offsetParent !== null && !sticky.disabled) {
      const label = pre.innerText.trim();
      if (label && sticky.textContent.trim() !== label) { sticky.dataset.label ||= sticky.textContent.trim(); sticky.textContent = label; }
    } else if (sticky.dataset.label && sticky.textContent.trim() !== sticky.dataset.label) {
      sticky.textContent = sticky.dataset.label;
    }
  };
  new MutationObserver(sync).observe(info, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
  sync();
})();
